import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import { useCustomer } from '../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderSetupForm = () => {
  const { customer } = useCustomer();
  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const hostName = useMemo(
    () => getDefaultHostName({ firstName: customer.firstName, lastName: customer.lastName }),
    [customer.firstName, customer.lastName],
  );

  const rules = useMemo(
    () => ({
      name: {
        required: formatMessage(messages.hostNameRequired),
        maxLength: {
          value: FIELD_MAX_LENGTH,
          message: formatMessage(messages.hostNameMaxLength, { max_length: FIELD_MAX_LENGTH }),
        },
      },
    }),
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  const form = useForm<GroupOrderSetupFormData>({
    defaultValues: { hostName },
    mode: 'onChange',
  });

  // ─────────────────────────────────────────────────────────────────────

  return {
    form,
    rules,
    maxLengths: { name: FIELD_MAX_LENGTH },
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Drops the last name initial before truncating, so the prefill never starts
 * over the limit and silently disables submit.
 */
function getDefaultHostName(params: Params) {
  const { firstName, lastName } = params;

  const lastNameInitial = lastName ? `${lastName.charAt(0)}.` : undefined;
  const fullName = [firstName, lastNameInitial].filter(Boolean).join(' ');

  if (fullName.length <= FIELD_MAX_LENGTH) {
    return fullName;
  }

  return (firstName ?? '').slice(0, FIELD_MAX_LENGTH).trim();
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly hostNameRequired: NoMessageValues;
  readonly hostNameMaxLength: { readonly max_length: MessageValue };
}>({
  hostNameRequired: {
    defaultMessage: 'Name is required',
    description: 'Group Order Setup | Host name required error',
  },
  hostNameMaxLength: {
    defaultMessage: 'Name must be {max_length} characters or fewer',
    description: 'Group Order Setup | Host name max length error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Ops limit so participant names are not cut off on printed chits.
 */
const FIELD_MAX_LENGTH = 14;

// ─── Types ───────────────────────────────────────────────────────────────────

export type GroupOrderSetupFormData = {
  hostName: string;
};

type Params = Pick<ReturnType<typeof useCustomer>['customer'], 'firstName' | 'lastName'>;
